"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog"
import { Button } from "../ui/button"
import { useToast } from "../hooks/use-toast"
import { X } from "lucide-react"
import { Badge } from "../ui/badge"

interface LeaveDayDetail {
  date: string; // YYYY-MM-DD
  type: "Full Day" | "First-Half" | "Second-Half";
}

interface CancelLeaveConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (leaveId: number, cancelledDays?: LeaveDayDetail[]) => void
  leaveId: number | null
  leaveDayDetails?: LeaveDayDetail[]
}

type CancellationType = "day-wise" | "whole" | null

export function CancelLeaveConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  leaveId,
  leaveDayDetails = []
}: CancelLeaveConfirmationModalProps) {
  const { toast } = useToast()
  // Check if leave is for only 1 day
  const isSingleDayLeave = leaveDayDetails.length === 1
  const [cancellationType, setCancellationType] = useState<CancellationType>(null)
  const [selectedDaysToCancel, setSelectedDaysToCancel] = useState<Set<string>>(new Set())
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // Auto-select "whole" if single day leave, otherwise reset to null
      setCancellationType(isSingleDayLeave ? "whole" : null)
      setSelectedDaysToCancel(new Set())
      setShowFinalConfirmation(false)
    }
  }, [isOpen, isSingleDayLeave])

  const handleDayToggle = (date: string) => {
    setSelectedDaysToCancel(prev => {
      const newSet = new Set(prev)
      if (newSet.has(date)) {
        newSet.delete(date)
      } else {
        newSet.add(date)
      }
      return newSet
    })
  }

  const handleRemoveDay = (date: string) => {
    setSelectedDaysToCancel(prev => {
      const newSet = new Set(prev)
      newSet.delete(date)
      return newSet
    })
  }

  const handleSave = () => {
    if (cancellationType === "whole") {
      setShowFinalConfirmation(true)
    } else if (cancellationType === "day-wise") {
      if (selectedDaysToCancel.size === 0) {
        toast({
          title: "Error",
          description: "Please select at least one day to cancel.",
          duration: 3000,
          className: "border border-red-300",
        })
        return
      }
      
      // Check if all days are selected
      if (selectedDaysToCancel.size === leaveDayDetails.length) {
        toast({
          title: "Cannot Cancel All Days",
          description: "You've selected all days for cancellation. Please use 'Whole Leave Request' option instead to cancel the entire leave.",
          duration: 4000,
          className: "border border-orange-300 bg-orange-50",
        })
        return
      }
      
      setShowFinalConfirmation(true)
    }
  }

  const handleFinalConfirm = () => {
    if (leaveId === null) return

    if (cancellationType === "whole") {
      onConfirm(leaveId)
    } else if (cancellationType === "day-wise") {
      const cancelledDays = leaveDayDetails.filter(day => selectedDaysToCancel.has(day.date))
      onConfirm(leaveId, cancelledDays)
    }
    
    setShowFinalConfirmation(false)
    onClose()
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  if (showFinalConfirmation) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md bg-white border border-gray-200 rounded-lg shadow-lg">
          <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6">
            <DialogTitle className="text-xl font-bold text-gray-900">Confirm Cancellation</DialogTitle>
          </DialogHeader>
          <div className="py-4 px-6 text-gray-700">
            {cancellationType === "whole" 
              ? "Are you sure you want to cancel this leave request?"
              : `Are you sure you want to cancel ${selectedDaysToCancel.size} selected day(s)?`
            }
          </div>
          <DialogFooter className="flex justify-end gap-3 pt-4 px-6 border-t border-gray-200">
            <Button 
              variant="outline" 
              onClick={() => setShowFinalConfirmation(false)} 
              className="text-gray-700 hover:bg-gray-100"
            >
              No
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleFinalConfirm} 
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6">
          <DialogTitle className="text-xl font-bold text-gray-900">Cancel Leave Request</DialogTitle>
        </DialogHeader>
        
        <div className="py-4 px-6">
          {/* Toggle Switch */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Cancellation Type
            </label>
            
            {/* Toggle Switch Container */}
            <div className="relative inline-flex items-center bg-gray-100 rounded-full p-1 w-full max-w-md">
              <button
                type="button"
                onClick={() => !isSingleDayLeave && setCancellationType("day-wise")}
                disabled={isSingleDayLeave}
                className={`flex-1 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 z-10 ${
                  cancellationType === "day-wise"
                    ? "bg-blue-600 text-white shadow-md"
                    : isSingleDayLeave
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                Day-wise Cancellation
              </button>
              <button
                type="button"
                onClick={() => setCancellationType("whole")}
                className={`flex-1 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 z-10 ${
                  cancellationType === "whole"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                Whole Leave Request
              </button>
            </div>

            {/* Info Message for Single Day Leave */}
            {isSingleDayLeave && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Day-wise cancellation is not available for single-day leaves. Please use "Whole Leave Request" option.
                </p>
              </div>
            )}
          </div>

          {/* Day-wise Cancellation UI */}
          {cancellationType === "day-wise" && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Days to Cancel ({selectedDaysToCancel.size} selected)
              </label>
              <div className="border border-gray-200 rounded-lg p-4 max-h-80 overflow-y-auto">
                {leaveDayDetails.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    No leave days available
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {leaveDayDetails.map((day, index) => {
                      const isSelected = selectedDaysToCancel.has(day.date)
                      return (
                        <div
                          key={`${day.date}-${index}`}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isSelected 
                              ? "bg-red-50 border-red-300" 
                              : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleDayToggle(day.date)}
                              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 text-sm">
                                {formatDate(day.date)}
                              </div>
                              <Badge 
                                variant="outline" 
                                className={`mt-1 text-xs ${
                                  day.type === "Full Day" 
                                    ? "bg-blue-100 text-blue-700" 
                                    : "bg-purple-100 text-purple-700"
                                }`}
                              >
                                {day.type}
                              </Badge>
                            </div>
                          </div>
                          {isSelected && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveDay(day.date)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-100 flex-shrink-0 ml-2"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Whole Leave Cancellation Message */}
          {cancellationType === "whole" && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                This will cancel the entire leave request. All days will be cancelled.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-end gap-3 pt-4 px-6 border-t border-gray-200">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleSave} 
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={!cancellationType}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
